import { useQuery } from "@tanstack/react-query";
import { api, edenFetch } from "@/lib/eden";

/**
 * Card-nav data, sourced live from `/api/catalog/card-nav`. The admin curates which
 * categories appear via the per-category "Show in card nav" toggle (`is_in_card_nav`)
 * and orders them with `position` (reorder endpoint). Replaces the old hardcoded mirror
 * of the deployed `/api/1/card-nav` payload.
 */
export type NavChild = { label: string; path: string };
export type NavCategory = {
  label: string;
  /** Category full_path — feeds the typed `/cards/$` link. */
  path: string;
  /** Brand accent, assigned by nav position (old `.card-category-{n}` hover colors). */
  accent: string;
  /** Flyout image (the category's poster/primary still, BDAY-37); null until uploaded. */
  imageUrl: string | null;
  children: NavChild[];
};

// The DB carries no accent, so cycle the old per-category hover colors by nav position.
const ACCENTS = ["#17a17e", "#3b87ab", "#ff867d", "#d0982f", "#fa2630", "#8a55a8"];

/** Short label preferred (old `shortCategoryName`), falling back to the full name. */
const label = (name: string, short: string | null) => short || name;

/**
 * One upcoming holiday for the Holiday tab (live from `/api/catalog/holiday-nav`).
 * `full_path` is the linked card category (ties it to its nav tab); `next_on` is the
 * next date (ISO) or null for an as-yet-undated variable holiday.
 */
export type HolidayNavItem = {
  occasion_id: string;
  occasion_key: string;
  occasion_name: string;
  full_path: string;
  next_on: string | null;
};

/**
 * Upcoming holidays for the card-nav Holiday tab, soonest-first. The Holiday tab
 * shows these (with dates) instead of its static subcategories — the old app's
 * live holiday list. Empty until the occasion catalog is seeded, in which case the
 * tab quietly falls back to its normal subcategory menu.
 */
export function useHolidayNav({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["/api/catalog/holiday-nav"],
    queryFn: () => edenFetch(api.api.catalog["holiday-nav"].get()),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * All published primary-nav top-level categories (live from `/api/catalog/nav`,
 * the same feed the logged-in /home page renders — shared query key, shared cache).
 * The "All Cards" directory lists the subset of these NOT already curated into the
 * card-nav bar: the job the old app's "MORE" overflow tab did (BDAY-149 #3).
 */
export function usePrimaryNav({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["/api/catalog/nav"],
    queryFn: () => edenFetch(api.api.catalog.nav.get()),
    enabled,
    staleTime: 5 * 60_000,
    select: (rows): NavChild[] => rows.map((r) => ({ label: r.name, path: r.full_path })),
  });
}

// Live card-nav categories mapped to the component's shape. `enabled: false`
// holds the request until the data is shown (e.g. the header's mobile menu).
export function useCardNav({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["/api/catalog/card-nav"],
    queryFn: () => edenFetch(api.api.catalog["card-nav"].get()),
    enabled,
    staleTime: 5 * 60_000,
    select: (rows): NavCategory[] =>
      rows.map((r, i) => ({
        label: label(r.name, r.short_name),
        path: r.full_path,
        accent: ACCENTS[i % ACCENTS.length] ?? "#666666",
        imageUrl: r.image_url ?? null,
        children: r.children.map((c) => ({
          label: label(c.name, c.short_name),
          path: c.full_path,
        })),
      })),
  });
}
